﻿import { TileProps } from '../Components/Tiles/tiles';
import { Game, LobbyTile, LobbyTileType, Winner } from '../apiTypes/GameService';
import { gameDataAdapter } from '../Components/Tiles/gameDataAdapter';
import useFavorite from '../hooks/useFavorite';
import useLiveData from '../hooks/useLiveData';
import {
  CATEGORY_TILE_TAGLINE,
  formatNumberToUnit,
  GAME_TITLE_MISSING,
  getCasinoPathname,
  getDateDiff
} from './tileHelper';
import useCategoryLiveData from '../hooks/useCategoryLiveData';
import useIsLoggedIn from '@SharedScripts/Hooks/useIsLoggedIn';
import { initiateLogin } from '@SharedScripts/UserData/Authentication';
import { FavoriteSessionKey, MegaTileInitIndex, SearchInitStateSessionKey } from '../Constants';
import { toUrlFriendlyName } from './stringConverter';
import { FavoriteSessionObject, SearchContextEnum } from '../Types';
import { getUrlWithoutParameters } from '@SharedScripts/Utils/Url';

type LobbyGame = Game & {
  tileType?: LobbyTileType;
  subtitle?: string;
  primaryButtonText?: string;
  primaryButtonUrl?: string;
  secondaryButtonText?: string;
  secondaryButtonUrl?: string;
  primaryButtonAs?: 'a' | 'button'
  secondaryButtonAs?: 'a' | 'button'
}

const favouriteBg = '../../assets/images/megatilebg_superfavourite.jpg';
const favouriteFg = '../../assets/images/megatilefg_superfavourite.png';

export const searchGameData = (games: Game[], isSuperFavoriteSelector: boolean) => getLobbyGameData(games, false, null, isSuperFavoriteSelector);

export const getGameData = (games: Game[], isPending: boolean, elementId: string | null = null, isSuperFavoriteSelector = false) => getLobbyGameData(games, isPending, elementId, isSuperFavoriteSelector);

// NB! This logic copied to 'useFilter.ts'. This is a temporary optimization fix
// If editing this logic, remember to also change the 'useFilter.ts'
export const getLobbyGameData = (games: LobbyGame[], isPending: boolean, elementId: string | null, isSuperFavoriteSelector = false) => {
  const isLoggedIn = useIsLoggedIn();
  const liveData = useLiveData(games);
  const categoryLiveData = useCategoryLiveData(games.flatMap((g) => (g.tileType === LobbyTileType.CATEGORY && g.id ? g.id : [])));
  const { addFavorite, removeFavorite, removeSuperFavorite, addSuperFavorite, favorites, highlightsIds } = useFavorite();

  const adaptedGames = games.map((game) => {
    const gameId = game.id ?? '';
    const isFavorite = favorites.includes(gameId);
    const isSuperFavorite = highlightsIds.includes(gameId);
    const isSuperFavoriteSelection = (!isSuperFavorite && game.tileType === LobbyTileType.SUPER_FAVORITE) || isSuperFavoriteSelector;
    const favouriteCallback = isSuperFavoriteSelection
      ? (gameId: string) => {
        if (isLoggedIn) {
          if (gameId) {
            addSuperFavorite(gameId);
            document.dispatchEvent(new CustomEvent('casino:search:close'));
          } else {
            document.dispatchEvent(new CustomEvent('casino:search:open', { detail: { context: SearchContextEnum.SuperFavorite } }));
          }
        } else {
          sessionStorage.setItem(SearchInitStateSessionKey, JSON.stringify(SearchContextEnum.SuperFavorite));
          // Using title because Superfavorit does not have an id
          sessionStorage.setItem(MegaTileInitIndex, JSON.stringify(game.title));
          initiateLogin();
          return;
        }
      }
      : isSuperFavorite
        ? (gameId: string) => removeSuperFavorite(gameId)
        : isFavorite
          ? (gameId: string) => removeFavorite(gameId)
          : (gameId: string) => {
            if (isLoggedIn) {
              addFavorite(gameId);
            } else {
              const sessionData: FavoriteSessionObject = { type: SearchContextEnum.Normal, id: gameId };
              sessionStorage.setItem(FavoriteSessionKey, JSON.stringify(sessionData));
              initiateLogin();
            }
          };

    return gameDataAdapter({
      elementId: elementId,
      gameData: game,
      liveData: liveData?.find(
        (gameLiveData) => gameLiveData?.id === game.id
      ),
      categoryLiveData: categoryLiveData?.find((data) => data.categoryId === game.id),
      favorited: isFavorite,
      isLoading: isPending,
      subtitle: game.subtitle,
      primaryButtonCallback: () => favouriteCallback(gameId),
      primaryButtonText: isSuperFavoriteSelection || game.tileType === LobbyTileType.CAMPAIGN || game.tileType === LobbyTileType.CATEGORY ? game.primaryButtonText : undefined,
      secondaryButtonHref: game.secondaryButtonUrl ?? '',
      secondaryButtonText: game.tileType === LobbyTileType.CAMPAIGN ? game.secondaryButtonText : undefined,
      buttonEnabled: isSuperFavoriteSelection || game.tileType === LobbyTileType.CAMPAIGN || game.tileType === LobbyTileType.CATEGORY,
      primaryButtonHref: game.primaryButtonUrl ?? ''
    });
  });

  return adaptedGames;
};

export const getWinnerData = (winner: Winner | undefined, elementId: string, isPending: boolean): TileProps[] => {
  if (!winner) return [];
  const pathName = getCasinoPathname();
  return winner.winnerList?.map((winner) => {
    const friendlyName = toUrlFriendlyName(winner.gameTitle || '');
    const navigationUrlWithLaunchParam = `/${pathName}/spil/${friendlyName}?launch&fullscreen&returnUrl=${encodeURI(getUrlWithoutParameters(location.href))}`;
    return {
      id: winner.id ?? '',
      alias: `${winner.userAlias} vandt` ?? '',
      prize: winner.prize ? formatNumberToUnit(Math.floor(parseFloat(winner.prize.replace(/,/g, '')))) : '',
      winMinutes: winner.winDate ? `for ${getDateDiff(winner.winDate)} min. siden` : '',
      title: winner.gameTitle ?? GAME_TITLE_MISSING,
      // TODO: Add gameType to winner object
      // type: winner.gameType ?? GameTypes.CASINO,
      navigationUrl: `/${pathName}/spil/${friendlyName}}`,
      mobileNavigationUrl: `/${pathName}/spil/${friendlyName}}`,
      navigationUrlWithLaunchParam,
      gameGraphics: {
        backgroundUrl: winner.thumbnail?.url ?? '',
        thumbnailUrl: winner.thumbnail?.url ?? '',
        foregroundUrl: winner.foreground?.url ?? '',
        logoUrl: winner.logo?.url ?? ''
      },
      isLoading: isPending
    };
  }) || [];
};

export const mapLobbyGameData = ({ title, description, subtitle, header, graphics, tileType, expandCategoryText, primaryButtonText, primaryButtonUrl, secondaryButtonText, secondaryButtonUrl, id }: LobbyTile): LobbyGame => {
  const pathName = getCasinoPathname();
  return {
    id: id ?? undefined,
    title: title ?? GAME_TITLE_MISSING,
    description: { full: (tileType === LobbyTileType.CAMPAIGN ? subtitle : description) || undefined },
    subtitle: (tileType === LobbyTileType.CAMPAIGN ? description : undefined) ?? undefined,
    tagLine: title !== header ? header : tileType === LobbyTileType.CATEGORY ? CATEGORY_TILE_TAGLINE : undefined,
    graphics: {
      background: graphics?.background ?? { url: favouriteBg },
      foreground: graphics?.foreground ?? { url: favouriteFg },
      logo: graphics?.foreground ?? { url: favouriteFg }
    },
    tileType,
    primaryButtonText: (tileType === LobbyTileType.CATEGORY ? `Gå til ${title}` : tileType === LobbyTileType.CAMPAIGN ? primaryButtonText : expandCategoryText) ?? undefined,
    primaryButtonUrl: primaryButtonUrl ? primaryButtonUrl : tileType !== LobbyTileType.SUPER_FAVORITE ? `/${pathName}/live/kategori/${id}` : '',
    secondaryButtonText: (tileType === LobbyTileType.CAMPAIGN ? secondaryButtonText : expandCategoryText) ?? undefined,
    secondaryButtonUrl: secondaryButtonUrl || ''
  };
};

export const overrideGameData = ({ header, tileType, expandCategoryText }: LobbyTile, lobbyGame: Game | undefined): LobbyGame => {

  return {
    ...lobbyGame,
    tagLine: header,
    tileType,
    primaryButtonText: expandCategoryText ?? undefined
  };
};
